import { createFeature, createReducer, on } from '@ngrx/store';
import {
  DevopsIdentity,
  DevopsOverview,
  DevopsPlatformIdentity,
  DevopsProxy,
  DevopsWorkspaceSelection,
} from '../../services/devops.service';
import { DevopsActions } from './devops.actions';

export const DEFAULT_DEVOPS_CAPABILITIES: DevopsOverview['capabilities'] = {
  auth: true,
  proxy: true,
  platform: false,
  deployments: false,
  builds: false,
  logs: false,
  incidents: false,
};

export interface DevopsState extends DevopsWorkspaceSelection {
  loading: boolean;
  busy: boolean;
  error: string | null;
  identity: DevopsIdentity | null;
  platform: DevopsPlatformIdentity | null;
  proxy: DevopsProxy | null;
  capabilities: DevopsOverview['capabilities'];
  lastLoadedAt: number | null;
}

const initialDevopsState: DevopsState = {
  loading: false,
  busy: false,
  error: null,
  identity: null,
  platform: null,
  proxy: null,
  capabilities: DEFAULT_DEVOPS_CAPABILITIES,
  teams: [],
  selectedTeamId: null,
  selectedTeam: null,
  selectedSpaceId: null,
  selectedCapsuleId: null,
  selectedBuildId: null,
  spaces: [],
  spaceGroups: [],
  capsules: [],
  webDeployments: [],
  builds: [],
  buildLog: null,
  incidents: [],
  notes: [],
  projectName: null,
  projectRepoCount: 0,
  matchedSpaceCount: 0,
  matchedCapsuleCount: 0,
  showingFallbackSpaces: false,
  lastLoadedAt: null,
};

export const devopsFeature = createFeature({
  name: 'devops',
  reducer: createReducer(
    initialDevopsState,
    on(DevopsActions.loadStarted, (state): DevopsState => ({ ...state, loading: true, error: null })),
    on(DevopsActions.overviewLoaded, (state, { snapshot }): DevopsState => ({
      ...state,
      ...snapshot,
      loading: false,
      lastLoadedAt: Date.now(),
    })),
    on(DevopsActions.loadFailed, (state, { error }): DevopsState => ({ ...state, error, loading: false })),
    on(DevopsActions.busyStarted, (state): DevopsState => ({ ...state, busy: true, error: null })),
    on(DevopsActions.busyEnded, (state): DevopsState => ({ ...state, busy: false })),
    on(DevopsActions.failed, (state, { error }): DevopsState => ({ ...state, error })),
    on(DevopsActions.identitySet, (state, { identity }): DevopsState => ({ ...state, identity })),
    on(DevopsActions.platformSet, (state, { platform }): DevopsState => ({ ...state, platform })),
    on(DevopsActions.proxySet, (state, { proxy }): DevopsState => ({ ...state, proxy })),
    on(DevopsActions.selectionPatched, (state, { selection }): DevopsState => ({ ...state, ...selection })),
    on(DevopsActions.loggedOut, (state): DevopsState => ({
      ...initialDevopsState,
      busy: state.busy,
      capabilities: state.capabilities,
      identity: { authenticated: false, email: null, message: 'Logged out', checkedAt: Date.now() } as DevopsIdentity,
      platform: {
        authenticated: false,
        email: null,
        message: 'Platform session cleared',
        checkedAt: Date.now(),
      } as DevopsPlatformIdentity,
      incidents: state.incidents,
      notes: state.notes,
      lastLoadedAt: state.lastLoadedAt,
    })),
    on(DevopsActions.reset, (): DevopsState => initialDevopsState)
  ),
});
